import type { DeploymentIncident, IncidentStatus } from '@central/api';
import { StatusChip, color, formatDateTime } from '@central/ui';

/** Where a staff incident stands, in customer wording. */
const incidentStatusLine: Record<IncidentStatus, string> = {
  investigating: 'Rethink is investigating',
  identified: 'Rethink is working on it',
  monitoring: 'A fix is in place and Rethink is monitoring',
  resolved: 'Resolved',
};

/** "Oct 9, 02:14"; just "02:14" when it falls on the same UTC day as `same`. */
function at(iso: string, same?: string): string {
  const text = formatDateTime(iso);
  if (!same) return text;
  const [day, time] = text.split(', ');
  return day === formatDateTime(same).split(', ')[0] ? time! : text;
}

/** The line under an incident's title: its window, or where an open one stands. */
function incidentWhen(n: DeploymentIncident, suffix: boolean, now = new Date()): string {
  if (!n.endedAt) {
    const parts = [`Since ${at(n.startedAt, now.toISOString())}`];
    if (n.planned) parts.push('planned');
    else if (n.status) parts.push(incidentStatusLine[n.status]);
    if (n.nextUpdateBy) parts.push(`next update by ${at(n.nextUpdateBy, now.toISOString())}`);
    return parts.join(' · ');
  }
  const range = `${formatDateTime(n.startedAt)} – ${at(n.endedAt, n.startedAt)}`;
  if (!suffix) return range;
  return `${range} · ${n.planned ? 'planned' : 'resolved'}`;
}

/**
 * A deployment's incidents. `grouped` (a shared environment) lists the open
 * ones first, each chipped Ongoing or Resolved; otherwise each row carries its
 * impact (Down or Degraded) in the deployment's own order, newest first.
 */
export function IncidentList({
  incidents,
  grouped,
}: {
  incidents: DeploymentIncident[];
  grouped: boolean;
}) {
  if (incidents.length === 0)
    return (
      <p style={{ margin: 0, padding: '0 20px 16px', color: color.textMuted }}>
        No incidents in the last 90 days.
      </p>
    );
  const rows = grouped
    ? [...incidents.filter((n) => !n.endedAt), ...incidents.filter((n) => n.endedAt)]
    : incidents;
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {rows.map((n) => {
        const latest = n.updates.at(-1);
        return (
          <li
            key={n.id}
            style={{
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
              padding: '12px 20px',
              borderTop: `1px solid ${color.rowRule}`,
            }}
          >
            {grouped ? (
              n.endedAt ? (
                <StatusChip tone="ok">Resolved</StatusChip>
              ) : (
                <StatusChip tone="warn">Ongoing</StatusChip>
              )
            ) : n.severity === 'minor' ? (
              <StatusChip tone="warn">Degraded</StatusChip>
            ) : (
              <StatusChip tone="down">Down</StatusChip>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14 }}>
              <span style={{ fontWeight: 600 }}>{n.title}</span>
              <span style={{ color: color.textMuted, fontSize: 13 }}>
                {incidentWhen(n, !grouped)}
              </span>
              {latest && !n.endedAt && (
                <span style={{ color: color.textBody, fontSize: 13 }}>
                  {at(latest.at, new Date().toISOString())} · {latest.body}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
